<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { real_id } from '../../../helpers/interface_helpers';
	import { isLastAPConnection } from '../NetworkComponents/network_helpers';
	import { onSettingsResponse } from '../../../helpers/device_settings_helpers';
	import { hasSecureProvisioning } from '../../../helpers/fw_1_7_5_helpers';
	//Services
	import { gen2Request } from '../../../services/gen_2_http_reqs';
	//Stores
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Design system components
	import { Toggle, Checkbox, Button, Alert } from '@Shelly/shelly-design-system';
	//Components
	import NetworkConnectedStatus from '../NetworkComponents/NetworkConnectedStatus.svelte';
	import NetworkModal from '../NetworkComponents/NetworkModal.svelte';
	import SSIDForm from '../NetworkComponents/SSIDForm.svelte';
	//Types
	import type { SettingRequest } from '../../../types/requests/settings/setting';
	import type { Device } from '../../../types/Device';
	import { capFirstChar } from '../../../helpers/utilities';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';

	$: settingsStore = getDeviceSettings(id);
	$: statusStore = getDeviceStatus(id);
	export let device: Device;
	export let isOffline;

	const id = real_id(device.id),
		settings = $settingsStore;

	const fwVersion = settings?.sys?.device?.fw_id || settings?.DeviceInfo?.fw_id;
	const isSecureProvisioning = hasSecureProvisioning({
		provision: settings?.provision,
		ver: fwVersion,
	});

	let enable = settings?.wifi?.ap?.enable,
		is_open = isSecureProvisioning ? false : settings?.wifi?.ap?.is_open,
		ssid = settings?.wifi?.ap?.ssid,
		waitForResponse = false,
		ssidForm: SSIDForm,
		modal: NetworkModal,
		postReqData: {
			enable: boolean;
			is_open?: boolean;
			pass?: string;
		};

	const connect = async () => {
			waitForResponse = true;
			const params = {
					config: {
						ap: postReqData,
					},
				},
				res: SettingRequest = await gen2Request(device, 'Wifi.SetConfig', params);
			if (res.isok) {
				settings.wifi.ap = { ...settings.wifi.ap, ...postReqData };
				devices_settings.update_devices_settings(id, settings);
			}
			onSettingsResponse(res, device, {
				success: $format('ap_scc_toast', {
					default: 'Access point settings saved.',
				}),
			});
			waitForResponse = false;
		},
		validate = () => {
			postReqData = { enable };
			if (enable) {
				const ssidParams = ssidForm.validate();
				if (!ssidParams) return false;
				postReqData.pass = ssidParams.pass;
				postReqData.is_open = ssidParams.is_open;
			}
			modal.open(!enable && isLastAPConnection(id));
		};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({ onClick: validate, loading: waitForResponse, disabled: isOffline });
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

{#if $statusStore?.wifi?.ap_client_count && settings?.wifi.ap?.range_extender?.enable}
	<NetworkConnectedStatus connected={true}>
		{$format('connected_devices', { default: 'Connected devices:' })}
		&nbsp;{$statusStore?.wifi?.ap_client_count}
	</NetworkConnectedStatus>
{/if}

{#if settings?.zigbee?.enable}
	<Alert
		class="mb-s"
		type="info"
		heading={$format('zigbee_mode_active', {
			default: 'Zigbee Mode Active',
		})}
		isHeadingBold
		message={$format('zigbee_mode_ap_info', {
			default: 'Access Point settings cannot be modified while Zigbee mode is enabled.',
		})}
	/>
{/if}
{#if $showNewLayout}
	<Checkbox
		size="l"
		class="mb-s-plus"
		bind:checked={enable}
		disabled={isOffline || settings?.zigbee?.enable}
		id="toggle-0z27s54"
	>
		{capFirstChar($format('sub_settings_title_wifi_mode_ap', { default: 'access point' }))}
	</Checkbox>

	{#if isSecureProvisioning}
		<Alert
			class="mb-s"
			type="warning"
			heading={$format('provision_password_mandatory_heading', {
				default: 'Password mandatory',
			})}
			isHeadingBold
			message={$format('provision_password_mandatory_message', {
				default: 'For your security this feature requires setting a network password.',
			})}
		/>
	{:else}
		<Alert
			class="mb-s"
			type="warning"
			heading={$format('password_recommendation', {
				default: 'Password Recommendation',
			})}
			isHeadingBold
			message={$format('important_to_password_protect_your_device', {
				default:
					"It is important to password protect your device's access point to secure your network from unauthorized access.",
			})}
		/>
	{/if}

	<SSIDForm
		bind:this={ssidForm}
		{ssid}
		{is_open}
		is_open_enabled={!isSecureProvisioning}
		disableForm={!enable || isOffline}
		readonly={true}
		withoutBackground
		removeBottomPadding
		removePaddings
	/>
{:else}
	<Checkbox
		size="l"
		class="mb-s"
		bind:checked={enable}
		disabled={isOffline || settings?.zigbee?.enable}
		id="checkbox-0z27s54"
	>
		{capFirstChar($format('option_enable', { default: 'enable' }))}
	</Checkbox>

	<hr class="mb-m mt-s" />

	{#if isSecureProvisioning}
		<Alert
			class="mb-s"
			type="warning"
			heading={$format('provision_password_mandatory_heading', {
				default: 'Password mandatory',
			})}
			isHeadingBold
			message={$format('provision_password_mandatory_message', {
				default: 'For your security this feature requires setting a network password.',
			})}
		/>
	{:else}
		<Alert
			class="mb-s"
			type="warning"
			heading={$format('password_recommendation', {
				default: 'Password Recommendation',
			})}
			isHeadingBold
			message={$format('important_to_password_protect_your_device', {
				default:
					"It is important to password protect your device's access point to secure your network from unauthorized access.",
			})}
		/>
	{/if}

	<SSIDForm
		bind:this={ssidForm}
		{ssid}
		{is_open}
		is_open_enabled={!isSecureProvisioning}
		disableForm={!enable || isOffline}
		readonly={true}
		withoutBackground
	/>

	<Button size="m" width="full" on:click={validate} loading={waitForResponse} disabled={isOffline}>
		{$format('button_save', { default: 'Save' })}
	</Button>
{/if}

<NetworkModal
	title={enable
		? $format('enable_access_point_modal_title', {
				default: 'Enable Access Point?',
			})
		: $format('disable_access_point_mmodal_title', {
				default: 'Disable Access Point?',
			})}
	btnTexts={{
		cancelText: '',
		saveText: enable ? $format('button_enable') : $format('disable'),
	}}
	bind:this={modal}
	on:confirm={connect}
	{isOffline}
>
	{$format('enable_disable_ap_msg', {
		values: {
			enable_disable: enable ? $format('option_enable') : $format('option_disable'),
			default: `Are you sure you want to {enable_disable} Access point for this device?`,
		},
	})}
</NetworkModal>
